// src/AdminKpis.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';
import {
  BarChart3,
  CheckCircle,
  Clock,
  Car,
  ShieldCheck,
  FileSignature,
  MessageCircle,
  RefreshCw,
  Calendar,
  AlertTriangle,
  MailX,
  Users,
} from 'lucide-react';

function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toFixed(2)}%`;
}
function fmtNum(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${Number(v)}`;
}
function fmtMin(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)} min`;
}
function fmtHours(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)} h`;
}
function fmtDateTime(v) {
  if (!v) return '—';
  const s = String(v);
  return s.includes('T') ? s.replace('T', ' ').slice(0, 16) : s.slice(0, 16);
}
function safeArray(v) {
  return Array.isArray(v) ? v : [];
}
function safeText(v, fallback = '—') {
  const s = String(v ?? '').trim();
  return s || fallback;
}

function Card({ title, icon: Icon, value, sub, tone = 'sky' }) {
  const toneMap = {
    sky: 'border-sky-200 bg-white',
    emerald: 'border-emerald-200 bg-white',
    amber: 'border-amber-200 bg-white',
    violet: 'border-violet-200 bg-white',
    slate: 'border-slate-200 bg-white',
  };

  return (
    <div className={`rounded-3xl border ${toneMap[tone] || toneMap.sky} shadow-sm p-5`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-slate-600">{title}</div>
        {Icon ? (
          <div className="h-9 w-9 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center">
            <Icon className="h-5 w-5 text-slate-700" />
          </div>
        ) : null}
      </div>
      <div className="mt-2 text-3xl font-semibold text-slate-900">{value}</div>
      {sub ? <div className="mt-1 text-sm text-slate-600">{sub}</div> : null}
    </div>
  );
}

function KpiPanel({ title, sub, right, children }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="p-5 flex items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-slate-900">{title}</div>
          {sub ? <div className="text-sm text-slate-600">{sub}</div> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-slate-600 text-sm">
      {children}
    </div>
  );
}

function CompactTable({ columns, rows, empty, getKey }) {
  if (!rows.length) return <EmptyState>{empty}</EmptyState>;

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2 font-semibold text-slate-700 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={getKey ? getKey(row, idx) : idx} className="border-t border-slate-200">
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.className || 'text-slate-700'}`}
                >
                  {col.render ? col.render(row, idx) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminKpis() {
  const today = useMemo(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, []);

  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, []);

  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(today);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);

  async function load() {
    try {
      setLoading(true);
      setErr('');

      const params = { start, end }; // org admin: no organization_id param
      const res = await API.get('/admin/kpis', { params });
      setData(res.data || null);
    } catch (e) {
      setData(null);
      setErr(e?.response?.data?.error || e?.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opsByDay = Array.isArray(data?.ops_load_by_day) ? data.ops_load_by_day : [];
  const cancellationSummary = data?.cancellation_summary || {};
  const cancellationsByReason = safeArray(data?.cancellations_by_reason);
  const cancellationsByTeacher = safeArray(data?.cancellations_by_teacher);
  const cancellationsByFamily = safeArray(data?.cancellations_by_family);
  const associatedParentCancellations = safeArray(data?.associated_parent_cancellations);
  const topEmailCancelParents = safeArray(data?.top_email_cancel_parents);

  return (
    <div className="p-4 sm:p-6">
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-sky-600 via-sky-500 to-indigo-600 text-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-white/90 text-sm">
              <BarChart3 className="h-4 w-4" />
              <span>Admin</span>
              <span className="text-white/60">/</span>
              <span>KPI Dashboard</span>
            </div>
            <h1 className="mt-2 text-2xl sm:text-3xl font-semibold">Operational ROI Metrics</h1>
            <p className="mt-1 text-white/90 text-sm">
              Track kept visits, parent confirmations, substitutions speed, drive time, EVV, G-Sign and ops load.
            </p>
          </div>

          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-2xl bg-white/15 hover:bg-white/20 border border-white/20 px-4 py-2 text-sm font-medium disabled:opacity-60"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Controls */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-2xl bg-white/10 border border-white/15 p-3">
            <label className="text-xs text-white/80 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Start
            </label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-2 w-full rounded-xl bg-white/90 text-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white/60"
            />
          </div>

          <div className="rounded-2xl bg-white/10 border border-white/15 p-3">
            <label className="text-xs text-white/80 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              End
            </label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-2 w-full rounded-xl bg-white/90 text-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white/60"
            />
          </div>

          <div className="rounded-2xl bg-white/10 border border-white/15 p-3 flex items-end">
            <button
              onClick={load}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-slate-900 hover:bg-white/90 px-4 py-2 text-sm font-semibold disabled:opacity-60"
            >
              <BarChart3 className="h-4 w-4" />
              Load KPIs
            </button>
          </div>
        </div>
      </div>

      {err ? (
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
          {err}
        </div>
      ) : null}

      {/* KPI Cards */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card
          title="Kept Visit Rate"
          icon={CheckCircle}
          value={fmtPct(data?.kept_rate_pct)}
          sub={`${fmtNum(data?.kept_visits)} kept / ${fmtNum(data?.scheduled_visits)} scheduled`}
          tone="emerald"
        />
        <Card
          title="Parent Accept Rate"
          icon={MessageCircle}
          value={fmtPct(data?.accept_rate_pct)}
          sub={`${fmtNum(data?.visits_accepted)} accepted / ${fmtNum(data?.visits_notified)} notified`}
          tone="sky"
        />
        <Card
          title="Late Cancel Rate (under 12h)"
          icon={Clock}
          value={fmtPct(data?.parent_late_cancel_pct)}
          sub={`${fmtNum(data?.parent_late_cancels_under_12h)} late / ${fmtNum(data?.parent_cancels_total)} parent cancels`}
          tone="amber"
        />
        <Card
          title="Time-to-Cover (avg)"
          icon={Clock}
          value={data?.avg_minutes_to_cover == null ? '—' : fmtMin(data.avg_minutes_to_cover)}
          sub={
            data?.max_minutes_to_cover == null
              ? 'Needs audit action: visit_reassign_pending'
              : `Max: ${fmtMin(data.max_minutes_to_cover)}`
          }
          tone="violet"
        />

        <Card
          title="Drive min / kept visit"
          icon={Car}
          value={data?.drive_min_per_kept_visit == null ? '—' : `${Number(data.drive_min_per_kept_visit).toFixed(2)} min`}
          sub={`Total: ${fmtHours(data?.drive_hours)} (${fmtNum(data?.kept_visits_with_timelog)} visits with route)`}
          tone="slate"
        />
        <Card
          title="EVV Completion Rate"
          icon={ShieldCheck}
          value={fmtPct(data?.evv_completion_pct)}
          sub={`${fmtNum(data?.evv_complete_visits)} complete / ${fmtNum(data?.evv_required_visits)} required (exceptions: ${fmtNum(
            data?.evv_exceptions
          )})`}
          tone="sky"
        />
        <Card
          title="EVV Strict GPS OK"
          icon={ShieldCheck}
          value={fmtPct(data?.evv_strict_gps_ok_pct)}
          sub="Has check-in/out time + GPS lat/lon present"
          tone="emerald"
        />
        <Card
          title="G-Sign Confirmation Rate"
          icon={FileSignature}
          value={fmtPct(data?.gsign_rate_pct)}
          sub={`${fmtNum(data?.gsign_confirmed_visits)} confirmed / ${fmtNum(data?.gsign_total_visits)} visits`}
          tone="slate"
        />
      </div>

      {/* Cancellation Intelligence */}
      <div className="mt-6 space-y-4">
        <KpiPanel
          title="Cancellation Intelligence"
          sub="Canceled visits by reason, provider, family context, and confirmed parent email cancel clicks."
          right={
            <div className="h-10 w-10 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-amber-700" />
            </div>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <Card
              title="Canceled Visits"
              icon={AlertTriangle}
              value={fmtNum(cancellationSummary.canceled_visits)}
              sub={`${fmtNum(data?.scheduled_visits)} scheduled in range`}
              tone="amber"
            />
            <Card
              title="Cancel Rate"
              icon={BarChart3}
              value={fmtPct(cancellationSummary.cancel_rate_pct)}
              sub="Canceled / scheduled visits"
              tone="amber"
            />
            <Card
              title="Family-Origin Cancels"
              icon={Users}
              value={fmtNum(cancellationSummary.family_origin_cancels)}
              sub={`${fmtNum(cancellationSummary.late_cancellations)} late, ${fmtNum(cancellationSummary.noshow_with_confirmation)} no-show`}
              tone="violet"
            />
            <Card
              title="Parent Email Cancels"
              icon={MailX}
              value={fmtNum(cancellationSummary.parent_email_cancels)}
              sub="Confirmed cancel clicks from parent email links"
              tone="sky"
            />
          </div>

          <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div>
              <div className="mb-2 text-sm font-semibold text-slate-900">By reason</div>
              <CompactTable
                rows={cancellationsByReason}
                empty="No canceled visits found for this range."
                columns={[
                  {
                    key: 'label',
                    label: 'Reason',
                    render: (r) => <span className="font-medium text-slate-900">{safeText(r.label || r.reason_label || r.reason_code)}</span>,
                  },
                  { key: 'count', label: 'Visits', align: 'right', className: 'text-slate-900 font-medium', render: (r) => fmtNum(r.count) },
                  { key: 'pct', label: 'Share', align: 'right', render: (r) => fmtPct(r.pct) },
                ]}
              />
            </div>

            <div>
              <div className="mb-2 text-sm font-semibold text-slate-900">By provider</div>
              <CompactTable
                rows={cancellationsByTeacher}
                empty="No provider cancellation breakdown found for this range."
                columns={[
                  {
                    key: 'teacher_name',
                    label: 'Provider',
                    render: (r) => <span className="font-medium text-slate-900">{safeText(r.teacher_name, `Teacher #${r.teacher_id || '—'}`)}</span>,
                  },
                  { key: 'total_canceled', label: 'Total', align: 'right', className: 'text-slate-900 font-medium', render: (r) => fmtNum(r.total_canceled) },
                  { key: 'teacher_canceled', label: 'Teacher', align: 'right', render: (r) => fmtNum(r.teacher_canceled) },
                  { key: 'family_canceled', label: 'Family', align: 'right', render: (r) => fmtNum(r.family_canceled) },
                  { key: 'late_cancellation', label: 'Late', align: 'right', render: (r) => fmtNum(r.late_cancellation) },
                  { key: 'noshow_with_confirmation', label: 'No-show', align: 'right', render: (r) => fmtNum(r.noshow_with_confirmation) },
                ]}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div>
              <div className="mb-2 text-sm font-semibold text-slate-900">Families with most canceled visits</div>
              <CompactTable
                rows={cancellationsByFamily}
                empty="No family cancellation context found for this range."
                columns={[
                  {
                    key: 'student_name',
                    label: 'Family',
                    render: (r) => (
                      <div>
                        <div className="font-medium text-slate-900">{safeText(r.student_name, `Student #${r.student_id || '—'}`)}</div>
                        <div className="text-xs text-slate-500">{safeText(r.parent_labels, 'No linked parents')}</div>
                      </div>
                    ),
                  },
                  { key: 'total_canceled', label: 'Canceled', align: 'right', className: 'text-slate-900 font-medium', render: (r) => fmtNum(r.total_canceled) },
                  { key: 'main_reason', label: 'Main reason', render: (r) => safeText(r.main_reason) },
                  { key: 'last_canceled_at', label: 'Last', render: (r) => fmtDateTime(r.last_canceled_at) },
                ]}
              />
            </div>

            <div>
              <div className="mb-2 text-sm font-semibold text-slate-900">Associated parents</div>
              <CompactTable
                rows={associatedParentCancellations}
                empty="No associated parent cancellation context found for this range."
                columns={[
                  {
                    key: 'parent_name',
                    label: 'Parent',
                    render: (r) => (
                      <div>
                        <div className="font-medium text-slate-900">{safeText(r.parent_name, `Parent #${r.parent_id || '—'}`)}</div>
                        <div className="text-xs text-slate-500">{safeText(r.parent_email)}</div>
                      </div>
                    ),
                  },
                  { key: 'associated_canceled_visits', label: 'Associated', align: 'right', className: 'text-slate-900 font-medium', render: (r) => fmtNum(r.associated_canceled_visits) },
                  { key: 'parent_email_clicks', label: 'Email clicks', align: 'right', render: (r) => fmtNum(r.parent_email_clicks) },
                  { key: 'linked_students', label: 'Linked families', render: (r) => safeText(r.linked_students) },
                ]}
              />
              <div className="mt-2 text-xs text-slate-500">
                Associated parents are linked through the student's family record. Confirmed email cancels are counted separately from parent notification clicks.
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-2 text-sm font-semibold text-slate-900">Confirmed parent email cancel clicks</div>
            <CompactTable
              rows={topEmailCancelParents}
              empty="No confirmed parent email cancel clicks found for this range."
              columns={[
                {
                  key: 'parent_name',
                  label: 'Parent',
                  render: (r) => (
                    <div>
                      <div className="font-medium text-slate-900">{safeText(r.parent_name, `Parent #${r.parent_id || '—'}`)}</div>
                      <div className="text-xs text-slate-500">{safeText(r.parent_email)}</div>
                    </div>
                  ),
                },
                { key: 'email_cancel_clicks', label: 'Clicks', align: 'right', className: 'text-slate-900 font-medium', render: (r) => fmtNum(r.email_cancel_clicks) },
                { key: 'distinct_visits', label: 'Visits', align: 'right', render: (r) => fmtNum(r.distinct_visits) },
                { key: 'last_response_at', label: 'Last click', render: (r) => fmtDateTime(r.last_response_at) },
              ]}
            />
          </div>
        </KpiPanel>
      </div>

      {/* Ops Load */}
      <div className="mt-6 rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="p-5 flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-slate-900">Ops Load</div>
            <div className="text-sm text-slate-600">
              Proxy for scheduling “chaos”: number of key operational actions in audit log.
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-semibold text-slate-900">{fmtNum(data?.ops_load_events_total)}</div>
            <div className="text-xs text-slate-500">events in range</div>
          </div>
        </div>

        <div className="px-5 pb-5">
          {opsByDay.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-slate-600 text-sm">
              No ops-load breakdown found (or no matching audit actions in this range).
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-slate-700">Day</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-700">Events</th>
                  </tr>
                </thead>
                <tbody>
                  {opsByDay.map((r, idx) => (
                    <tr key={idx} className="border-t border-slate-200">
                      <td className="px-3 py-2 text-slate-700">{r.day}</td>
                      <td className="px-3 py-2 text-slate-900 font-medium">{r.events}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 text-xs text-slate-500">
            Actions included: create/update/delete, bulk scheduling, substitutions, EVV resolve, domain notes updates
          </div>
        </div>
      </div>
    </div>
  );
}
